// src/pages/beta-community/BetaEditInscription.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import "./BetaInscriptionForm.css";

const BetaEditInscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [inscription, setInscription] = useState(null);

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    fonction: "",
    societe: "",
    profil: "",
    nb_oeuvres: "",
    nb_clients: "",
    plan: "",
    outils_actuels: "",
    pain_points: "",
    motivation: "",
    disponibilite_feedback: "",
    temoignage: "",
    disponibilite_appel: "",
  });

  const loadInscription = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError("");

      // RPC SECURITY DEFINER : la table n'est plus lisible directement, le token est vérifié
      // côté serveur (correctif sécurité sur beta_inscriptions).
      const { data, error: fetchError } = await supabase.rpc("get_beta_inscription_by_token", {
        p_token: token,
      });

      if (fetchError || !data?.valid) {
        setInscription(null);
        setError("Lien invalide ou expiré. Contactez-nous à support@artkiv.com");
        return;
      }

      setInscription(data);
      setFormData({
        nom: data.nom || "",
        prenom: data.prenom || "",
        email: data.email || "",
        telephone: data.telephone || "",
        fonction: data.fonction || "",
        societe: data.societe || "",
        profil: data.profil || "",
        nb_oeuvres: data.nb_oeuvres || "",
        nb_clients: data.nb_clients || "",
        plan: data.plan || "",
        outils_actuels: data.outils_actuels || "",
        pain_points: data.pain_points || "",
        motivation: data.motivation || "",
        disponibilite_feedback: data.disponibilite_feedback || "",
        temoignage: data.temoignage || "",
        disponibilite_appel: data.disponibilite_appel || "",
      });
    } catch (err) {
      console.error("Erreur chargement:", err);
      setError("Erreur lors du chargement de vos informations.");
      setInscription(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError("Token manquant. Veuillez utiliser le lien reçu par email.");
      setLoading(false);
      return;
    }
    loadInscription();
  }, [token, loadInscription]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      // (Optionnel) mini validation soft, sans casser le flow
      if (!formData.email?.trim()) {
        throw new Error("Veuillez renseigner votre email.");
      }
      if (!formData.nom?.trim() || !formData.prenom?.trim()) {
        throw new Error("Veuillez renseigner votre nom et prénom.");
      }

      const { data: result, error: updateError } = await supabase.rpc(
        "update_beta_inscription_by_token",
        { p_token: token, p_data: formData }
      );

      if (updateError) throw updateError;
      if (!result?.success) {
        throw new Error(
          result?.error === "invalid_token"
            ? "Lien invalide ou expiré. Contactez-nous à support@artkiv.com"
            : "Erreur lors de la sauvegarde. Veuillez réessayer."
        );
      }

      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Erreur sauvegarde:", err);
      setError(err?.message || "Erreur lors de la sauvegarde. Veuillez réessayer.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="beta-inscription-page" style={{ fontFamily: "'Georgia', serif" }}>
        <div className="container">
          <div className="loading-container">
            <div className="loader"></div>
            <p>Chargement de vos informations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="beta-inscription-page" style={{ fontFamily: "'Georgia', serif" }}>
        <div className="container">
          <div className="success-container">
            <div className="success-icon">✓</div>
            <h1 className="success-title">Modifications enregistrées !</h1>
            <p className="success-text">Vos informations ont été mises à jour avec succès.</p>
            <button className="btn btn-primary" onClick={() => navigate("/rejoindre-la-communaute")}>
              Retour à l&apos;accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!inscription) {
    return (
      <div className="beta-inscription-page" style={{ fontFamily: "'Georgia', serif" }}>
        <div className="container">
          <div className="error-container">
            <h1>⚠️ Erreur</h1>
            <p>{error}</p>
            <button className="btn btn-secondary" onClick={() => navigate("/rejoindre-la-communaute")}>
              Retour à l&apos;accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="beta-inscription-page" style={{ fontFamily: "'Georgia', serif" }}>
      <div className="form-header">
        <h1 className="form-logo">ARTKIV</h1>
        <p className="form-subtitle">Modifier mon inscription Beta Community</p>
      </div>

      <div className="container">
        <div className="form-intro">
          <p className="intro-highlight">Bonjour {formData.prenom || "!"}</p>
          <p>Vous pouvez modifier vos informations ci-dessous. Les champs marqués d&apos;un * sont obligatoires.</p>
        </div>

        {error && (
          <div className="error-message">
            <strong>Erreur :</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="beta-form">
          <div className="form-section">
            <h2>Informations personnelles</h2>

            <div className="form-row">
              <div className="form-group">
                <label>Nom *</label>
                <input name="nom" value={formData.nom} onChange={handleChange} required disabled={saving} />
              </div>
              <div className="form-group">
                <label>Prénom *</label>
                <input name="prenom" value={formData.prenom} onChange={handleChange} required disabled={saving} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={saving}
                />
              </div>
              <div className="form-group">
                <label>Téléphone</label>
                <input name="telephone" value={formData.telephone} onChange={handleChange} disabled={saving} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Profil</h2>

            <div className="form-row">
              <div className="form-group">
                <label>Fonction</label>
                <input name="fonction" value={formData.fonction} onChange={handleChange} disabled={saving} />
              </div>
              <div className="form-group">
                <label>Société</label>
                <input name="societe" value={formData.societe} onChange={handleChange} disabled={saving} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Profil</label>
                <select name="profil" value={formData.profil} onChange={handleChange} disabled={saving}>
                  <option value="">— Sélectionner —</option>
                  <option value="collectionneur">Collectionneur</option>
                  <option value="family_office">Family Office / CGP</option>
                  <option value="art_advisor">Art Advisor / Consultant</option>
                  <option value="assureur">Assureur</option>
                  <option value="expert">Expert</option>
                  <option value="autre">Autre</option>
                </select>
              </div>

              <div className="form-group">
                <label>Plan souhaité</label>
                <select name="plan" value={formData.plan} onChange={handleChange} disabled={saving}>
                  <option value="">— Sélectionner —</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="art_advisor">Art Advisor</option>
                  <option value="art_advisor_premium">Art Advisor Premium</option>
                  <option value="family_office">Family Office / CGP</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Nombre d&apos;œuvres</label>
                <input name="nb_oeuvres" value={formData.nb_oeuvres} onChange={handleChange} disabled={saving} />
              </div>
              <div className="form-group">
                <label>Nombre de clients (si pro)</label>
                <input name="nb_clients" value={formData.nb_clients} onChange={handleChange} disabled={saving} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Contexte</h2>

            <div className="form-group">
              <label>Outils actuels</label>
              <textarea
                name="outils_actuels"
                value={formData.outils_actuels}
                onChange={handleChange}
                disabled={saving}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Pain points</label>
              <textarea
                name="pain_points"
                value={formData.pain_points}
                onChange={handleChange}
                disabled={saving}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Motivation</label>
              <textarea
                name="motivation"
                value={formData.motivation}
                onChange={handleChange}
                disabled={saving}
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Disponibilité pour feedback</label>
                <select
                  name="disponibilite_feedback"
                  value={formData.disponibilite_feedback}
                  onChange={handleChange}
                  disabled={saving}
                >
                  <option value="">—</option>
                  <option value="oui">Oui</option>
                  <option value="non">Non</option>
                  <option value="peut_etre">Peut-être</option>
                </select>
              </div>

              <div className="form-group">
                <label>Disponibilité pour un appel</label>
                <select
                  name="disponibilite_appel"
                  value={formData.disponibilite_appel}
                  onChange={handleChange}
                  disabled={saving}
                >
                  <option value="">—</option>
                  <option value="oui">Oui</option>
                  <option value="non">Non</option>
                  <option value="peut_etre">Peut-être</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Témoignage (optionnel)</label>
              <textarea
                name="temoignage"
                value={formData.temoignage}
                onChange={handleChange}
                disabled={saving}
                rows={3}
              />
            </div>
          </div>

          <div className="submit-container">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Sauvegarde..." : "Enregistrer les modifications"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate("/rejoindre-la-communaute")}>
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BetaEditInscription;
